/**
 * JourneyPane - Vertical phase navigator with structured/free mode toggle
 * Controls workspace context by setting current phase
 */

import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useJourneyStore } from '@/stores/journeyStore'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Lock, CheckCircle2, Circle, Target, Settings2, Sprout, Search, Map as MapIcon, Rocket } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { PhaseMode } from '@ispot/shared'

// Goal ID to icon mapping
const goalIcons: Record<string, React.ReactNode> = {
  'validate-new-idea': <Sprout className="w-5 h-5" />,
  'diagnose-stalled-growth': <Search className="w-5 h-5" />,
  'sharpen-positioning': <Target className="w-5 h-5" />,
  'prioritize-roadmap': <MapIcon className="w-5 h-5" />,
  'expand-new-market': <Rocket className="w-5 h-5" />,
}

// Mode badge colors
const modeColors: Record<PhaseMode, { bg: string; text: string; label: string }> = {
  full: { bg: 'bg-market/20', text: 'text-market', label: 'Full' },
  check: { bg: 'bg-business/20', text: 'text-business', label: 'Check' },
  review: { bg: 'bg-solution/20', text: 'text-solution', label: 'Review' },
  skip: { bg: 'bg-warm-200', text: 'text-text-muted', label: 'Skip' },
}

interface JourneyPaneProps {
  onOpenGoalWizard?: () => void
}

export function JourneyPane({ onOpenGoalWizard }: JourneyPaneProps) {
  const {
    journeyMode,
    setJourneyMode,
    currentPhaseId,
    setCurrentPhaseId,
    completedPhases,
  } = useWorkspaceStore()

  const {
    getActivePhases,
    getSelectedGoal,
    getPhaseProgress,
    isPhaseUnlocked,
    isLoading,
  } = useJourneyStore()

  const phases = getActivePhases()
  const selectedGoal = getSelectedGoal()

  const handlePhaseClick = (phaseId: string) => {
    const unlocked = journeyMode === 'free' || isPhaseUnlocked(phaseId)
    if (unlocked) {
      setCurrentPhaseId(phaseId)
    }
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-20 bg-warm-100 rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Goal Header */}
      {selectedGoal && (
        <div className="p-3 rounded-lg bg-warm-100 border border-border-subtle">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-market/20 text-market flex items-center justify-center">
                {goalIcons[selectedGoal.id] || <Target className="w-4 h-4" />}
              </div>
              <div>
                <p className="text-xs text-text-muted">Current Goal</p>
                <p className="font-medium text-sm text-text">{selectedGoal.name}</p>
              </div>
            </div>
            {onOpenGoalWizard && (
              <button
                onClick={onOpenGoalWizard}
                className="p-1.5 rounded hover:bg-warm-200 text-text-muted hover:text-text transition-colors"
                title="Change Goal"
              >
                <Settings2 size={14} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* No Goal Selected */}
      {!selectedGoal && onOpenGoalWizard && (
        <button
          onClick={onOpenGoalWizard}
          className="w-full p-3 rounded-lg border-2 border-dashed border-border-subtle hover:border-market hover:bg-market/5 transition-all text-left"
        >
          <div className="flex items-center gap-2">
            <Target size={16} className="text-market" />
            <span className="text-sm font-medium text-text">Select a Goal</span>
          </div>
          <p className="text-xs text-text-muted mt-1">
            Choose a goal to customize your journey
          </p>
        </button>
      )}

      {/* Mode Toggle */}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant={journeyMode === 'structured' ? 'default' : 'outline'}
          onClick={() => setJourneyMode('structured')}
          className="flex-1 text-xs"
          style={
            journeyMode === 'structured'
              ? {
                  backgroundColor: 'var(--color-warm-600)',
                  color: 'white',
                }
              : undefined
          }
        >
          Structured
        </Button>
        <Button
          size="sm"
          variant={journeyMode === 'free' ? 'default' : 'outline'}
          onClick={() => setJourneyMode('free')}
          className="flex-1 text-xs"
          style={
            journeyMode === 'free'
              ? {
                  backgroundColor: 'var(--color-warm-600)',
                  color: 'white',
                }
              : undefined
          }
        >
          Free
        </Button>
      </div>

      {/* Info Banner */}
      {journeyMode === 'structured' && (
        <div
          className="text-xs p-2 rounded"
          style={{
            backgroundColor: 'var(--color-warm-100)',
            color: 'var(--color-text-secondary)',
          }}
        >
          <Lock size={12} className="inline mr-1" />
          Phases unlock sequentially as you progress
        </div>
      )}

      {/* Phase List */}
      <div className="space-y-2">
        {phases.map((phase, index) => {
          const isActive = currentPhaseId === phase.id
          const phaseProgress = getPhaseProgress(phase.id)
          const isCompleted = phaseProgress >= 100 || completedPhases.has(phase.id)
          const unlocked = isPhaseUnlocked(phase.id)
          const isLocked = journeyMode === 'structured' && !unlocked
          const modeStyle = modeColors[phase.mode]

          return (
            <button
              key={phase.id}
              onClick={() => handlePhaseClick(phase.id)}
              disabled={isLocked}
              className={cn(
                'w-full p-3 rounded-lg border transition-all text-left',
                isActive && 'ring-2 ring-warm-400 bg-warm-50',
                !isActive && 'hover:bg-warm-50',
                isLocked && 'opacity-50 cursor-not-allowed'
              )}
              style={{
                borderColor: isActive ? phase.color : 'var(--color-border)',
              }}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium"
                    style={{ backgroundColor: phase.color || '#6B7B43' }}
                  >
                    {index + 1}
                  </div>
                  <span className="font-medium text-sm">{phase.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  {/* Mode Badge */}
                  {phase.mode !== 'full' && (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${modeStyle.bg} ${modeStyle.text}`}>
                      {modeStyle.label}
                    </span>
                  )}
                  <PhaseStatus
                    isCompleted={isCompleted}
                    isLocked={isLocked}
                    isActive={isActive}
                  />
                </div>
              </div>
              <Progress
                value={phaseProgress}
                className="h-1"
                style={{
                  backgroundColor: `${phase.color || '#6B7B43'}20`,
                }}
              />
              <div className="text-xs text-text-muted mt-1">
                {Math.round(phaseProgress)}% complete
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function PhaseStatus({
  isCompleted,
  isLocked,
  isActive,
}: {
  isCompleted: boolean
  isLocked: boolean
  isActive: boolean
}) {
  if (isCompleted) {
    return (
      <CheckCircle2 size={16} style={{ color: 'var(--color-success)' }} />
    )
  }

  if (isLocked) {
    return <Lock size={16} className="text-text-muted" />
  }

  if (isActive) {
    return <Circle size={16} style={{ color: 'var(--color-business)' }} />
  }

  return <Circle size={16} className="text-text-muted" />
}
